import { useLanguage } from '@/hooks/use-language.hook';
import { ffTheme } from '@/theme/theme';
import { createTheme, ThemeProvider } from '@mui/material';
import { enUS, esES, viVN, zhCN } from '@mui/material/locale';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import {
  enUS as dateFnsEn,
  es as dateFnsEs,
  vi as dateFnsVi,
  zhCN as dateFnsZh
} from 'date-fns/locale';
import { useMemo, type FunctionComponent, type ReactNode } from 'react';

import type { SupportedLocale } from '@/lib/i18n/config';

const muiCoreLocales: Record<SupportedLocale, typeof enUS> = {
  en: enUS,
  es: esES,
  zh: zhCN,
  vi: viVN
};

const dateFnsLocales: Record<SupportedLocale, typeof dateFnsEn> = {
  en: dateFnsEn,
  es: dateFnsEs,
  zh: dateFnsZh,
  vi: dateFnsVi
};

type LocalizedMuiProvidersProps = {
  children: ReactNode;
};

export const LocalizedMuiProviders: FunctionComponent<
  LocalizedMuiProvidersProps
> = ({ children }) => {
  const { language } = useLanguage();
  const themed = useMemo(
    () => createTheme(ffTheme, muiCoreLocales[language]),
    [language]
  );

  return (
    <ThemeProvider noSsr theme={themed} defaultMode="system">
      <LocalizationProvider
        dateAdapter={AdapterDateFns}
        adapterLocale={dateFnsLocales[language]}
        dateFormats={{
          keyboardDate: 'yyyy-MM-dd'
        }}
      >
        {children}
      </LocalizationProvider>
    </ThemeProvider>
  );
};
